import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Users, User, Trophy } from 'lucide-react';
import PageLoader from '../components/PageLoader';
import LoadMoreButton from '../components/LoadMoreButton';
import { authConfig } from '../config/auth';
import { useAuth } from '../contexts/AuthContext';
import { usePaginatedFetch } from '../hooks/usePaginatedFetch';
import type { Challenge, Submission } from '@crewcast/shared';
import { ChallengeStatus, resolveAssets } from '@crewcast/shared';
import AssetLightbox, { type LightboxAsset } from '../components/AssetLightbox';
import SubmissionForm from '../components/SubmissionForm';

interface LightboxState {
  assets: LightboxAsset[];
  index: number;
}
import { linkify } from '../utils/linkify';
import '../styles/challenges.css';
import '../styles/submissions.css';

type EditableChallengeFields = Pick<
  Challenge,
  'title' | 'description' | 'rules' | 'points' | 'collaboratorsEnabled' | 'status'
>;

export default function ChallengeDetailPage() {
  const { challengeId } = useParams<{ challengeId: string }>();
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [form, setForm] = useState<EditableChallengeFields | null>(null);
  const [pointsInput, setPointsInput] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [userSubmissions, setUserSubmissions] = useState<Submission[]>([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [lightbox, setLightbox] = useState<LightboxState | null>(null);

  const openLightbox = (submission: Submission, startIndex = 0) => {
    const assets = resolveAssets(submission).map((a) => ({ url: a.url, type: a.type }));
    if (!assets.length) return;
    setLightbox({ assets, index: startIndex });
  };

  const navigate = useNavigate();
  const { token, user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';

  const {
    items: challengeSubmissions,
    loading: loadingChallengeSubmissions,
    loadingMore: loadingMoreSubmissions,
    hasMore: hasMoreSubmissions,
    loadMore: loadMoreSubmissions,
  } = usePaginatedFetch<Submission>({
    url: `${authConfig.apiUrl}/api/admin/challenges/${challengeId}/submissions`,
    token,
    dataKey: 'submissions',
    limit: 20,
    enabled: isAdmin && !!challengeId,
  });

  useEffect(() => {
    if (challengeId) {
      fetchChallenge(challengeId);
      if (!isAdmin) {
        fetchUserSubmissionsForChallenge(challengeId);
      }
    } else {
      setError('Missing challenge id');
      setLoading(false);
    }
  }, [challengeId, isAdmin]);

  const fetchChallenge = async (id: string) => {
    setLoading(true);
    setError('');

    try {
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(
        `${authConfig.apiUrl}/api/admin/challenges/${id}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch challenge');
      }

      setChallenge(data.challenge);
      setForm({
        title: data.challenge.title,
        description: data.challenge.description,
        rules: data.challenge.rules,
        points: data.challenge.points,
        collaboratorsEnabled: data.challenge.collaboratorsEnabled,
        status: data.challenge.status,
      });
      setPointsInput(String(data.challenge.points));
      setImagePreview(data.challenge.image || '');
    } catch (err: any) {
      setError(err.message || 'Failed to fetch moment');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserSubmissionsForChallenge = async (id: string) => {
    setLoadingSubmissions(true);

    try {
      if (!token) return;

      const response = await fetch(
        `${authConfig.apiUrl}/api/submissions`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const challengeSubmissions = data.submissions.filter(
          (sub: Submission) => sub.challengeId === id
        );
        setUserSubmissions(challengeSubmissions);
      }
    } catch (err) {
      console.error('Failed to fetch submissions:', err);
    } finally {
      setLoadingSubmissions(false);
    }
  };

  const handleSubmissionSuccess = () => {
    // Refresh submissions after successful submission
    if (challengeId) {
      fetchUserSubmissionsForChallenge(challengeId);
    }
  };


  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!challengeId || !form) return;

    setSaveError('');
    setSaving(true);

    // Validate points
    const points = Number(pointsInput);
    if (isNaN(points) || points < 0 || pointsInput === '') {
      setSaveError('Please enter a valid positive number for points');
      setSaving(false);
      return;
    }

    try {
      if (!token) throw new Error('Not authenticated');

      let imageUrl: string | undefined;

      // Upload image if selected
      if (imageFile) {
        setUploadingImage(true);
        const presignedResponse = await fetch(
          `${authConfig.apiUrl}/api/upload/presigned-url`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              fileName: imageFile.name,
              fileType: imageFile.type,
              purpose: 'challenge',
            }),
          }
        );

        if (!presignedResponse.ok) {
          throw new Error('Failed to get upload URL');
        }

        const { uploadUrl, publicUrl } = await presignedResponse.json();

        // Upload to S3
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': imageFile.type,
          },
          body: imageFile,
        });

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload image');
        }

        imageUrl = publicUrl;
        setUploadingImage(false);
      }

      const payload = {
        title: form.title.trim(),
        description: form.description?.trim() || '',
        rules: form.rules?.trim() || '',
        points: points,
        collaboratorsEnabled: form.collaboratorsEnabled,
        status: form.status,
        image: imageUrl || challenge?.image || undefined,
      };

      if (!payload.title) {
        throw new Error('Title is required');
      }

      const response = await fetch(
        `${authConfig.apiUrl}/api/admin/challenges/${challengeId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update challenge');
      }

      setChallenge(data.challenge);
      setImageFile(null);
      alert('Moment updated successfully!');
    } catch (err: any) {
      setSaveError(err.message || 'Failed to update moment');
    } finally {
      setSaving(false);
      setUploadingImage(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      const validVideoTypes = ['video/mp4', 'video/quicktime', 'video/x-m4v'];
      
      if (!validImageTypes.includes(file.type) && !validVideoTypes.includes(file.type)) {
        alert('Please upload an image (JPG, PNG, GIF, WEBP) or video (MP4, MOV)');
        return;
      }

      // Validate file size (100MB max)
      const maxSize = 100 * 1024 * 1024; // 100MB
      if (file.size > maxSize) {
        alert(`File size must be less than 100MB (current: ${Math.round(file.size / (1024 * 1024))}MB)`);
        return;
      }

      setImageFile(file);
      if (file.type.startsWith('video/')) {
        setImagePreview(URL.createObjectURL(file));
      } else {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleDelete = async () => {
    if (!challengeId) return;
    if (!window.confirm('Are you sure you want to delete this moment?'))
      return;

    try {
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(
        `${authConfig.apiUrl}/api/admin/challenges/${challengeId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok && response.status !== 204) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to delete challenge');
      }

      navigate('/moments');
    } catch (err: any) {
      alert(err.message || 'Failed to delete challenge');
    }
  };

  if (loading) {
    return (
      <div className="challenge-detail-container">
        <PageLoader />
      </div>
    );
  }

  if (error || !challenge || !form) {
    return (
      <div className="challenge-detail-container">
        <div className="error">
          {error || 'Moment not found'}
          <button onClick={() => navigate('/moments')} className="back-btn">
            Back to Moments
          </button>
        </div>
      </div>
    );
  }

  // Employee View - Clean read-only view with submission form
  if (!isAdmin) {
    return (
      <div className="challenge-detail-container">
        <div className="challenge-detail-header">
          <button onClick={() => navigate('/moments')} className="back-btn">
            ← Back to Moments
          </button>
          <h1>Moment Details</h1>
        </div>

        <div className="challenge-view-card">
          {challenge.image && (
            <div className="challenge-hero-image">
              {challenge.image.match(/\.(mp4|mov|m4v)$/i) ? (
                <video src={`${challenge.image}#t=0.001`} controls playsInline preload="metadata" className="challenge-hero-video" />
              ) : (
                <img src={challenge.image} alt={challenge.title} />
              )}
            </div>
          )}

          <div className="challenge-header-info">
            <h2>{challenge.title}</h2>
            <div className="challenge-badges">
              <span className={`status-badge status-${challenge.status}`}>
                {challenge.status === 'active' ? '🟢 Active' :
                 challenge.status === 'complete' ? '✅ Complete' : '📝 Draft'}
              </span>
              {(challenge.points ?? 0) > 0 && (
                <span className="points-badge">
                  🏆 {challenge.points} points
                </span>
              )}
              {challenge.collaboratorsEnabled && (
                <span className="team-badge" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Users size={16} /> Team Moment
                </span>
              )}
            </div>
          </div>

          {challenge.description && (
            <div className="challenge-section">
              <h3>Description</h3>
              <p className="challenge-description">{linkify(challenge.description)}</p>
            </div>
          )}

          {challenge.rules && (
            <div className="challenge-section">
              <h3>Rules & Requirements</h3>
              <p className="challenge-rules">{linkify(challenge.rules)}</p>
            </div>
          )}

          <div className="challenge-section">
            <h3>Moment Details</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Type:</span>
                <span className="info-value">
                  {challenge.collaboratorsEnabled
                    ? <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Users size={16} /> Team Moment</span>
                    : <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><User size={16} /> Individual Moment</span>}
                </span>
              </div>
              {(challenge.points ?? 0) > 0 && (
                <div className="info-item">
                  <span className="info-label">Points Reward:</span>
                  <span className="info-value" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Trophy size={16} color="#00bf62" /> {challenge.points} points
                  </span>
                </div>
              )}
              <div className="info-item">
                <span className="info-label">Status:</span>
                <span className="info-value">
                  {challenge.status === 'active' ? 'Active - Submit Now!' :
                   challenge.status === 'complete' ? 'Completed' : 'Coming Soon'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Submission Form - Only show if challenge is active */}
        {challenge.status === 'active' && challengeId && (
          <div className="submission-section">
            <h2>Submit Your Entry</h2>
            <SubmissionForm
              challengeId={challengeId}
              collaboratorsEnabled={challenge.collaboratorsEnabled}
              onSuccess={handleSubmissionSuccess}
            />
          </div>
        )}

        {challenge.status !== 'active' && (
          <div className="challenge-inactive-notice">
            <p>
              {challenge.status === 'draft'
                ? '📝 This moment is not yet active. Check back soon!'
                : '✅ This moment has been completed and is no longer accepting submissions.'}
            </p>
          </div>
        )}

        {/* User's Submissions */}
        {userSubmissions.length > 0 && (
          <div className="user-submissions-section">
            <h2>Your Submissions</h2>
            {loadingSubmissions ? (
              <div className="loading">Loading submissions...</div>
            ) : (
              <div className="submissions-grid">
                {userSubmissions.map((submission) => {
                  const assets = resolveAssets(submission);
                  const primary = assets[0];
                  if (!primary?.url) return null;
                  const statusClassName = submission.approved === null ? 'pending' : submission.approved ? 'approved' : 'rejected';
                  const statusText = submission.approved === null ? '⏳ Pending Review' : submission.approved ? '✅ Approved' : '❌ Rejected';
                  return (
                    <div key={submission.submissionId} className="submission-card-large">
                      <div className="submission-preview">
                        {primary.type === 'video' ? (
                          <video src={`${primary.url}#t=0.001`} className="submission-media" muted playsInline preload="metadata" />
                        ) : (
                          <img src={primary.url} alt="Submission" className="submission-media" />
                        )}
                        {assets.length > 1 && (
                          <span className="submission-asset-count-badge">+{assets.length - 1} more</span>
                        )}
                        <span className={`submission-status-badge ${statusClassName}`}>{statusText}</span>
                        <button className="media-tap-target" onClick={() => openLightbox(submission)} aria-label="View submission" />
                      </div>
                      <div className="submission-card-content">
                        {submission.caption && (
                          <p className="submission-caption">{submission.caption}</p>
                        )}
                        {submission.approved === false && submission.rejectionReason && (
                          <div className="submission-rejection-reason">
                            <strong>Reason:</strong> {submission.rejectionReason}
                          </div>
                        )}
                        <div className="submission-metadata">
                          <div className="metadata-item">
                            <span className="metadata-label">Submitted:</span>
                            <span className="metadata-value">
                              {new Date(submission.submittedAt).toLocaleDateString()} at{' '}
                              {new Date(submission.submittedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          {submission.collaborators && submission.collaborators.length > 0 && (
                            <div className="metadata-item">
                              <span className="metadata-label">Team:</span>
                              <span className="metadata-value">👥 {submission.collaborators.length + 1} members</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

      {lightbox && (
        <AssetLightbox
          assets={lightbox.assets}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
          onIndexChange={(i) => setLightbox((lb) => lb ? { ...lb, index: i } : lb)}
        />
      )}
      </div>
    );
  }

  // Admin View - Existing edit form
  return (
      <div className="challenge-detail-container">
        <div className="challenge-detail-header">
          <button onClick={() => navigate('/moments')} className="back-btn">
            ← Back to Moments
          </button>
          <h1>{isAdmin ? 'Edit Moment' : 'Moment Details'}</h1>
        </div>

      <form onSubmit={handleSave} className="challenge-detail-form">
        <div className="form-section">
          <div className="form-group">
            <label htmlFor="title">Title *</label>
            <input
              type="text"
              id="title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              disabled={!isAdmin}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              disabled={!isAdmin}
              rows={5}
            />
          </div>

            <div className="form-group">
              <label htmlFor="rules">Rules</label>
              <textarea
                id="rules"
                value={form.rules}
                onChange={(e) => setForm({ ...form, rules: e.target.value })}
                disabled={!isAdmin}
                rows={5}
                placeholder="Moment rules and requirements..."
              />
          </div>

          <div className="form-group">
            <label htmlFor="points">Points *</label>
            <input
              type="text"
              id="points"
              value={pointsInput}
              onChange={(e) => {
                // Only allow digits
                const value = e.target.value.replace(/[^0-9]/g, '');
                setPointsInput(value);
              }}
              disabled={!isAdmin}
              placeholder="0"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              value={form.status}
              onChange={(e) =>
                setForm({
                  ...form,
                  status: e.target.value as ChallengeStatus,
                })
              }
              disabled={!isAdmin}
              className="status-select"
            >
              <option value={ChallengeStatus.DRAFT}>Draft</option>
              <option value={ChallengeStatus.ACTIVE}>Active</option>
              <option value={ChallengeStatus.COMPLETE}>Complete</option>
            </select>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={form.collaboratorsEnabled}
                onChange={(e) =>
                  setForm({
                    ...form,
                    collaboratorsEnabled: e.target.checked,
                  })
                }
                disabled={!isAdmin}
              />
              <span>Enable Collaborators (Team Moment)</span>
            </label>
          </div>

          {isAdmin && (
            <div className="form-group">
              <label htmlFor="image">Moment Image or Video</label>
              <p className="field-hint">
                Upload an image or video (max 100MB). Supported formats: JPG, PNG, GIF, WEBP, MP4, MOV
              </p>
              <input
                type="file"
                id="image"
                accept="image/*,video/mp4,video/quicktime,video/x-m4v"
                onChange={handleImageChange}
              />
              {imagePreview && (
                <div className="image-preview">
                  {imageFile?.type.startsWith('video/') || /\.(mp4|mov|m4v)$/i.test(imagePreview) ? (
                    <video src={imagePreview} controls playsInline preload="metadata" className="preview-video" />
                  ) : (
                    <img src={imagePreview} alt="Preview" />
                  )}
                </div>
              )}
            </div>
          )}

          {!isAdmin && challenge.image && (
            <div className="image-preview">
              {/\.(mp4|mov|m4v)$/i.test(challenge.image) ? (
                <video src={challenge.image} controls playsInline preload="metadata" className="preview-video" />
              ) : (
                <img src={challenge.image} alt={challenge.title} />
              )}
            </div>
          )}
        </div>

        {saveError && <div className="form-error">{saveError}</div>}

        {isAdmin && (
          <div className="form-actions">
            <button
              type="submit"
              disabled={saving || uploadingImage}
              className="save-btn"
            >
              {saving
                ? 'Saving...'
                : uploadingImage
                ? 'Uploading Image...'
                : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/moments')}
              className="cancel-btn"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className="delete-btn"
              disabled={saving}
            >
              Delete Moment
            </button>
          </div>
        )}

        {!isAdmin && (
          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/moments')}
              className="back-btn"
            >
              Back
            </button>
          </div>
        )}
      </form>

      <div className="challenge-info">
        <h3>Moment Information</h3>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Created:</span>
            <span className="info-value">
              {new Date(challenge.createdAt).toLocaleDateString()}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Last Updated:</span>
            <span className="info-value">
              {new Date(challenge.updatedAt).toLocaleDateString()}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Type:</span>
            <span className="info-value">
              {challenge.collaboratorsEnabled
                ? <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Users size={16} /> Team Moment</span>
                : <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><User size={16} /> Individual Moment</span>}
            </span>
          </div>
          {(challenge.points ?? 0) > 0 && (
            <div className="info-item">
              <span className="info-label">Points Reward:</span>
              <span className="info-value" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Trophy size={16} color="#00bf62" /> {challenge.points} points
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Admin: Challenge Submissions Section */}
      <div className="challenge-submissions-section">
        <div className="section-header">
          <h3>Submissions ({challengeSubmissions.length})</h3>
          <button
            onClick={() => navigate('/review-submissions')}
            className="view-all-btn"
          >
            View All Pending →
          </button>
        </div>

        {loadingChallengeSubmissions ? (
          <div className="loading">Loading submissions...</div>
        ) : challengeSubmissions.length === 0 ? (
          <div className="no-submissions-notice">
            <p>No submissions yet for this moment.</p>
          </div>
        ) : (
          <div className="admin-submissions-list">
            {challengeSubmissions.map((submission) => {
              const isPending = submission.approved === null;
              const isApproved = submission.approved === true;
              const isRejected = submission.approved === false;

              return (
                <div key={submission.submissionId} className="admin-submission-card">
                  <div className="admin-submission-thumbnail">
                    {(() => {
                      const assets = resolveAssets(submission);
                      const primary = assets[0];
                      return primary.type === 'video' ? (
                        <video src={`${primary.url}#t=0.001`} className="admin-submission-media" muted playsInline preload="metadata" />
                      ) : (
                        <img src={primary.url} alt="Submission" className="admin-submission-media" />
                      );
                    })()}
                    <span className={`admin-status-badge ${isPending ? 'pending' : isApproved ? 'approved' : 'rejected'}`}>
                      {isPending ? '⏳ Pending' : isApproved ? '✅ Approved' : '❌ Rejected'}
                    </span>
                    <button className="media-tap-target" onClick={() => openLightbox(submission)} aria-label="View submission" />
                  </div>
                  <div className="admin-submission-info">
                    {submission.caption && (
                      <p className="admin-submission-caption">{submission.caption}</p>
                    )}
                    <div className="admin-submission-meta">
                      <span className="admin-submission-date">
                        {new Date(submission.submittedAt).toLocaleDateString()}
                      </span>
                      {submission.collaborators && submission.collaborators.length > 0 && (
                        <span className="admin-submission-team">
                          👥 {submission.collaborators.length + 1} members
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <LoadMoreButton onClick={loadMoreSubmissions} loading={loadingMoreSubmissions} hasMore={hasMoreSubmissions} />
      </div>

      {lightbox && (
        <AssetLightbox
          assets={lightbox.assets}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
          onIndexChange={(i) => setLightbox((lb) => lb ? { ...lb, index: i } : lb)}
        />
      )}
    </div>
  );
}

